import { useEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { readInterpretation, skillLabels } from '../../store/selectors'
import { domainColor, domainLabel } from '../../lib/rubric'
import { EditExtraction } from './EditExtraction'

/**
 * What was just logged.
 *
 * The default path through this card is silence: it appears, it says what it
 * heard, and it leaves. A parent who is satisfied should be able to walk away
 * mid-sentence and lose nothing.
 *
 * It exists at all because the alternative is worse. An interpretation that
 * quietly becomes evidence for a level change, without the parent ever seeing
 * it, corrodes trust in everything built on top of it. So the correction is
 * always one tap away, and never required.
 */

const AUTO_DISMISS_MS = 9000

export function ExtractionCard({ captureId }: { captureId: string }) {
  const capture = useAuroraStore((s: any) => s.captures[captureId])
  const thinking = useAuroraStore((s: any) => s.capture.interpreting.includes(captureId))
  const online = useAuroraStore((s: any) => s.ui.online)
  const labels = useAuroraStore(skillLabels)

  const [editing, setEditing] = useState(false)
  const [held, setHeld] = useState(false)
  const timer = useRef<number | null>(null)

  const interpretation = readInterpretation(capture)
  // Offline there is nothing to wait for: the capture is safe, and reading it
  // happens on the way back. A spinner that can never finish is a lie.
  const queued = !interpretation && !online
  const waiting = !interpretation && online && thinking

  const close = () => {
    ;(auroraStore.getState() as any).clearInterpreting(captureId)
  }

  // Dismiss itself once there is something to read and the parent is not
  // reading it. Editing, hovering or keyboard focus all hold it open.
  useEffect(() => {
    if (waiting || editing || held) return
    timer.current = window.setTimeout(close, AUTO_DISMISS_MS)
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
      timer.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, editing, held, captureId])

  if (!capture) return null

  const said = String(capture.raw?.transcript ?? capture.raw?.text ?? '').trim()

  if (editing) {
    return (
      <EditExtraction
        captureId={captureId}
        interpretation={interpretation}
        transcript={said}
        onDone={() => {
          setEditing(false)
          close()
        }}
        onCancel={() => setEditing(false)}
      />
    )
  }

  const status = !online
    ? 'Saved, will sync'
    : thinking
      ? 'Making sense of that…'
      : 'Saved'

  return (
    <section
      className="extract"
      role="status"
      aria-label="What was just logged"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      <header className="extract-head">
        <span
          className={`extract-dot${thinking && online ? ' is-working' : ''}`}
          aria-hidden="true"
        />
        <span className="extract-label">{status}</span>
        <button className="extract-close" onClick={close} aria-label="Dismiss">
          <CloseIcon />
        </button>
      </header>

      {said && <p className="extract-said">{truncate(said, 180)}</p>}

      {waiting && (
        <div className="extract-waiting" aria-hidden="true">
          <span className="skeleton extract-skeleton" />
          <span className="skeleton extract-skeleton is-short" />
        </div>
      )}

      {queued && (
        <>
          <p className="extract-summary">
            Kept safely on this device. Sapling will read it once you are back online.
          </p>
          <div className="extract-actions">
            <button className="btn btn-primary btn-sm" onClick={close}>
              Got it
            </button>
          </div>
        </>
      )}

      {interpretation && (
        <>
          {interpretation.summary && (
            <p className="extract-summary">{interpretation.summary}</p>
          )}

          {interpretation.domains.length > 0 && (
            <ul className="extract-domains">
              {interpretation.domains.slice(0, 5).map((key) => (
                <li
                  key={key}
                  className="extract-domain"
                  style={{ '--domain-color': domainColor(key, 'var(--accent)') } as React.CSSProperties}
                >
                  {domainLabel(key)}
                </li>
              ))}
            </ul>
          )}

          {interpretation.skills.length > 0 && (
            <ul className="extract-skills">
              {interpretation.skills.slice(0, 4).map((skill, i) => {
                const name = labels[skill.name] ?? skill.name
                const moved =
                  skill.suggestedLevel !== undefined &&
                  skill.currentLevel !== undefined &&
                  skill.suggestedLevel !== skill.currentLevel
                return (
                  <li key={`${skill.name}:${i}`} className="extract-skill">
                    <span className="extract-skill-name">{name}</span>
                    {moved ? (
                      <span className="extract-move tnum">
                        {skill.currentLevel}
                        <ArrowIcon />
                        {skill.suggestedLevel}
                      </span>
                    ) : (
                      skill.signal && (
                        <span className="extract-signal">{skill.signal}</span>
                      )
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {interpretation.quotes[0] && (
            <blockquote className="extract-quote">
              <p className="extract-quote-text">“{interpretation.quotes[0]}”</p>
              <footer className="extract-quote-note">saved to her words</footer>
            </blockquote>
          )}

          <div className="extract-actions">
            <button className="btn btn-primary btn-sm" onClick={close}>
              Looks right
            </button>
            <button className="btn btn-quiet btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>
        </>
      )}
    </section>
  )
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1).trimEnd()}…`
}

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
)
